import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { LIVE_STATE_CODES, LOGO_URL, STATE_NAMES, stateSlug } from "@/utils/constants";
import { ECOSYSTEM_LINKS } from "@/utils/ecosystemLinks";
import { trackHubEvent } from "@/utils/hub";

const DISCOVER_LINKS = [
  ["/", "Directory", "footer-directory-link"],
  ["/map", "Map view", "footer-map-link"],
  ["/areas", "Areas we serve", "footer-areas-link"],
  ["/travel", "Travel mode", "footer-travel-link"],
  ["/wellness-hub", "Wellness Hub", "footer-wellness-hub-link"],
  ["/supplement-analysis", "Supplement analysis", "footer-supplement-analysis-link"],
];

const COMMUNITY_LINKS = [
  ["/events", "Events", "footer-events-link"],
  ["/challenges", "Challenges", "footer-challenges-link"],
  ["/community-partners", "Community partners", "footer-community-partners-link"],
  ["/founders-corner", "Founder’s Corner", "footer-founders-corner-link"],
  ["/review", "Leave a review", "footer-review-link"],
];

function FooterLinks({ items }) {
  return <div className="flex flex-col gap-3">{items.map(([to, label, testId]) => <Link key={to} to={to} className="font-body text-sm text-white/65 transition-colors hover:text-white" data-testid={testId}>{label}</Link>)}</div>;
}

export default function Footer() {
  return (
    <footer className="iw-grid-bg-dark mt-16 border-t border-white/10 bg-[#081334] text-white">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="Wellness Collective AI logo" className="h-14 w-14 object-contain" />
              <div>
                <div className="font-heading text-xl font-bold leading-tight">Wellness Collective AI</div>
                <div className="font-mono text-[10px] uppercase text-[#F45B63]">Discover · Connect · Thrive</div>
              </div>
            </div>
            <p className="mt-6 max-w-md font-body text-sm leading-relaxed text-white/65">A connected wellness search engine for real venues, trusted providers, practical education, and community experiences across the country.</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a href={ECOSYSTEM_LINKS.intelligence.home} target="_blank" rel="noopener noreferrer" onClick={() => trackHubEvent("ecosystem_cta_click", null, { destination: "integratednycai", placement: "footer" })} className="inline-flex items-center gap-2 border border-white/25 px-4 py-3 font-mono text-[10px] uppercase text-white transition-colors hover:border-[#F45B63]" data-testid="footer-integratednycai-link">Clinical intelligence <ArrowUpRight className="h-3.5 w-3.5" /></a>
              <a href={ECOSYSTEM_LINKS.optimize.home} target="_blank" rel="noopener noreferrer" onClick={() => trackHubEvent("ecosystem_cta_click", null, { destination: "iwoptimizeme", placement: "footer" })} className="inline-flex items-center gap-2 border border-white/25 px-4 py-3 font-mono text-[10px] uppercase text-white transition-colors hover:border-[#F45B63]" data-testid="footer-iwoptimizeme-link">Optimization programs <ArrowUpRight className="h-3.5 w-3.5" /></a>
              <a href={ECOSYSTEM_LINKS.labs.home} target="_blank" rel="noopener noreferrer" onClick={() => trackHubEvent("ecosystem_cta_click", null, { destination: "homebloodkit", placement: "footer" })} className="inline-flex items-center gap-2 border border-white/25 px-4 py-3 font-mono text-[10px] uppercase text-white transition-colors hover:border-[#F45B63]" data-testid="footer-homebloodkit-link">At-home lab kits <ArrowUpRight className="h-3.5 w-3.5" /></a>
            </div>
          </div>
          <div className="md:col-span-2">
            <h2 className="mb-5 font-mono text-xs uppercase text-white/45">Discover</h2>
            <FooterLinks items={DISCOVER_LINKS} />
          </div>
          <div className="md:col-span-2">
            <h2 className="mb-5 font-mono text-xs uppercase text-white/45">Community</h2>
            <FooterLinks items={COMMUNITY_LINKS} />
          </div>
          <div className="min-w-0 md:col-span-3">
            <h2 className="mb-5 font-mono text-xs uppercase text-white/45">For businesses</h2>
            <div className="flex flex-col gap-3">
              <Link to="/owner" className="font-body text-sm text-white/65 transition-colors hover:text-white" data-testid="footer-owner-link">Claim your venue</Link>
              <Link to="/advertise" className="font-body text-sm text-white/65 transition-colors hover:text-white" data-testid="footer-advertise-link">Advertise with us</Link>
              <Link to="/pricing" className="font-body text-sm text-white/65 transition-colors hover:text-white" data-testid="footer-pricing-link">Business pricing</Link>
              <a href="mailto:Info@IntegratedWellnessNY.com" className="break-all font-body text-sm text-white/65 transition-colors hover:text-white" data-testid="footer-email-link">Info@IntegratedWellnessNY.com</a>
              <a href="tel:2015353582" className="font-body text-sm text-white/65 transition-colors hover:text-white" data-testid="footer-phone-link">201.535.3582</a>
            </div>
          </div>
        </div>
        <nav className="mt-14 border-t border-white/15 pt-7" aria-label="Browse by state" data-testid="footer-state-links">
          <h2 className="mb-4 font-mono text-xs uppercase text-white/45">Browse wellness by state</h2>
          <ul className="flex flex-wrap gap-x-5 gap-y-2">
            {LIVE_STATE_CODES.map((code) => <li key={code}><Link to={`/state/${stateSlug(code)}`} className="font-body text-sm text-white/65 transition-colors hover:text-white" data-testid={`footer-state-${stateSlug(code)}`}>{STATE_NAMES[code]}</Link></li>)}
          </ul>
        </nav>
        <div className="mt-10 flex flex-col gap-5 border-t border-white/15 pt-7 sm:flex-row sm:items-start sm:justify-between">
          <p className="font-mono text-[10px] uppercase text-white/45">© 2026 Wellness Collective AI</p>
          <div className="flex flex-col gap-3 sm:items-end">
            <Link to="/admin" className="font-mono text-[10px] uppercase text-white/45 transition-colors hover:text-white" data-testid="footer-staff-login">Staff login</Link>
            <p className="max-w-xl text-left font-body text-xs leading-relaxed text-white/45 sm:text-right">Community wellness grades are educational recommendations, not medical diagnoses. Always consult your provider.</p>
          </div>
        </div>
      </div>
    </footer>
  );
}